import {
  ComposerAiFeature,
  ComposerAiFeatureResolutionsEnum,
  ComposerAiFeatureTracker,
  ComposerAiFeatureTypesEnum,
  ComposerAiResolution,
} from '../../../../libs/shared/core/src/helpers/ai/composeV2Helpers';
import {stringSimilarity} from '../../../../libs/shared/core/src/helpers/utilities/stringSimilarity';
import {DraftMessageModel} from '../../../../libs/shared/core/src/models/api/messageModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {ContentEditableHtmlEditor} from '../../common/editor/html/contentEditableHtmlEditor';

/*
 * Types.
 */

export interface EmailComposerTrackingManager {
  onAiMenuOpen(): void;
  onAiStart(feature: ComposerAiFeature, hasSelection: boolean): ComposerAiFeatureTracker;
  onSend(): void;
}

interface EmailComposerTrackingManagerParams {
  userId: number;
  companyId: number;
  message: DraftMessageModel;
  bodyEditor: ContentEditableHtmlEditor;
}

/*
 * Module.
 */

export const createEmailComposerTrackingManager = ({
  userId,
  companyId,
  message,
  bodyEditor,
}: EmailComposerTrackingManagerParams): EmailComposerTrackingManager => {
  const composerCreatedMillis = Date.now();
  const aiTrackers: Array<ComposerAiFeatureTracker> = [];

  const findLastWholeBodyResult = () => {
    for (const {hasSelection, resolution} of [...aiTrackers].reverse()) {
      if (!hasSelection && resolution?.type === ComposerAiFeatureResolutionsEnum.COMPLETED) {
        return resolution.result;
      }
    }

    return null;
  };

  const trackerManager: EmailComposerTrackingManager = {
    onAiMenuOpen() {
      trackInterfaceEvent(InterfaceEventsEnum.AI_COMPOSE_OPEN, {
        user_id: userId,
        company_id: companyId,
        message_uid: message.uid,
      });
    },

    onAiStart(feature, hasSelection) {
      const startTime = Date.now();

      trackInterfaceEvent(InterfaceEventsEnum.AI_COMPOSE_SELECT, {
        user_id: userId,
        company_id: companyId,
        message_uid: message.uid,
        function: feature.type,
        tone: feature.type === ComposerAiFeatureTypesEnum.CHANGE_TONE ? feature.tone : undefined,
        language: feature.type === ComposerAiFeatureTypesEnum.TRANSLATE ? feature.language : undefined,
      });

      let resolution: ComposerAiResolution | null = null;

      const tracker: ComposerAiFeatureTracker = {
        get feature() {
          return feature;
        },
        get hasSelection() {
          return hasSelection;
        },
        get resolution() {
          return resolution;
        },
        get startTime() {
          return startTime;
        },

        onCompleted(result) {
          if (resolution !== null) {
            return;
          }

          trackInterfaceEvent(InterfaceEventsEnum.AI_COMPOSE_SUCCESS, {
            user_id: userId,
            company_id: companyId,
            message_uid: message.uid,
            function: feature.type,
          });

          resolution = {
            type: ComposerAiFeatureResolutionsEnum.COMPLETED,
            time: Date.now(),
            result,
          };
        },

        onError() {
          if (resolution !== null) {
            return;
          }

          resolution = {
            type: ComposerAiFeatureResolutionsEnum.ERROR,
            time: Date.now(),
          };
        },

        onCancel() {
          if (resolution !== null) {
            return;
          }

          resolution = {
            type: ComposerAiFeatureResolutionsEnum.CANCELLED,
            time: Date.now(),
          };
        },

        onUndo(trigger) {
          if (resolution?.type !== ComposerAiFeatureResolutionsEnum.COMPLETED) {
            return;
          }

          trackInterfaceEvent(InterfaceEventsEnum.AI_COMPOSE_UNDO, {
            user_id: userId,
            company_id: companyId,
            message_uid: message.uid,
            function: feature.type,
            trigger,
          });

          resolution = {
            ...resolution,
            type: ComposerAiFeatureResolutionsEnum.UNDO,
          };
        },
      };

      aiTrackers.push(tracker);

      return tracker;
    },

    onSend() {
      const duration = Date.now() - composerCreatedMillis;
      const aiFeaturesCount = aiTrackers.length;
      const features = aiTrackers.map((t) => ({
        feature: t.feature,
        resolution: t.resolution?.type,
        duration: t.resolution ? t.resolution.time - t.startTime : undefined,
      }));

      trackInterfaceEvent(InterfaceEventsEnum.AI_COMPOSE_SEND, {
        user_id: userId,
        company_id: companyId,
        message_uid: message.uid,
        duration,
        features_count: aiFeaturesCount,
        features,
      });

      const lastWholeBodyResult = findLastWholeBodyResult();
      if (!lastWholeBodyResult) {
        return;
      }

      const similarity = stringSimilarity(lastWholeBodyResult, bodyEditor.services.text.text);

      trackInterfaceEvent(InterfaceEventsEnum.AI_COMPOSE_SEND_SIMILARITY, {
        version: 2,
        message_uid: message.uid,
        similarity,
      });
    },
  };

  return trackerManager;
};
